import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Helmet } from "@dr.pogodin/react-helmet";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import "../css/App.css";

// Emblem shown on the looping stage vs. the light setup card.
const STAGE_LOGO = "/SOLE-logo.svg";
const CARD_LOGO = "/SOLE-logo.svg";
const CANDLE_IMG = "/candledemo.jpg";
const CANDLE_URL = "https://soleministries.org/candle";
const DISCORD_URL = "https://discord.gg/8kUtNREn4D";

// How long a slide sits fully visible before the transition to the next begins.
const HOLD_MS = 13000;

// Entrance offsets (px). Exit is a gentle, shorter reverse of the same vector.
const FROM = {
    top: { x: 0, y: -54 },
    left: { x: -58, y: 0 },
    right: { x: 58, y: 0 },
    below: { x: 0, y: 46 },
    bottom: { x: 0, y: 58 },
};

// Firm settle, no overshoot / bounce.
const EASE_SETTLE = [0.22, 0.61, 0.36, 1];
const EASE_LEAVE = [0.4, 0, 1, 1];

/**
 * One slide element: fades in while sliding ~50px into place from `dir`,
 * staggered after the element before it. On slide change it drifts a little
 * back the way it came and fades out (reverse order: last in, first out).
 */
function Element({ dir, order, count, reduce, className, children }) {
    const off = reduce ? { x: 0, y: 0 } : FROM[dir];
    const enterDelay = order * 0.14;
    const leaveDelay = (count - 1 - order) * 0.05;
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, x: off.x, y: off.y }}
            animate={{
                opacity: 1,
                x: 0,
                y: 0,
                transition: { duration: reduce ? 0.3 : 0.42, delay: enterDelay, ease: EASE_SETTLE },
            }}
            exit={{
                opacity: 0,
                x: reduce ? 0 : off.x * 0.55,
                y: reduce ? 0 : off.y * 0.55,
                transition: { duration: 0.3, delay: leaveDelay, ease: EASE_LEAVE },
            }}
        >
            {children}
        </motion.div>
    );
}

function QrBlock({ value, caption }) {
    return (
        <div className="welcome-qr">
            <div className="welcome-qr-frame">
                <QRCodeSVG value={value} size={320} bgColor="#fbf5ea" fgColor="#0f3650" level="M" marginSize={2} />
            </div>
            <div className="welcome-qr-caption">{caption}</div>
        </div>
    );
}

function WelcomeSlide({ index, reduce }) {
    if (index === 0) {
        return (
            <>
                <Element dir="top" order={0} count={4} reduce={reduce} className="welcome-logo">
                    <img src={STAGE_LOGO} alt="SOLE" />
                </Element>
                <Element dir="left" order={1} count={4} reduce={reduce} className="welcome-headline">
                    Welcome to SOLE
                </Element>
                <Element dir="right" order={2} count={4} reduce={reduce} className="welcome-body">
                    Servants of Our Lady of Evangelization
                </Element>
                <Element dir="bottom" order={3} count={4} reduce={reduce} className="welcome-note">
                    The talk will begin shortly
                </Element>
            </>
        );
    }

    if (index === 1) {
        return (
            <>
                <Element dir="top" order={0} count={5} reduce={reduce} className="welcome-logo welcome-logo--sm">
                    <img src={STAGE_LOGO} alt="SOLE" />
                </Element>
                <Element dir="left" order={1} count={5} reduce={reduce} className="welcome-headline">
                    Support our mission
                </Element>
                <Element dir="below" order={2} count={5} reduce={reduce} className="welcome-body">
                    Bring the prayerful scent of the Mass into your home with an Altar Flame candle. Purchasing these candles allows SOLE to provide food continue our ministry.
                </Element>
                <div className="welcome-media-row">
                    <Element dir="left" order={3} count={5} reduce={reduce} className="welcome-candle">
                        <img src={CANDLE_IMG} alt="Altar Flame candle" />
                    </Element>
                    <Element dir="bottom" order={4} count={5} reduce={reduce} className="welcome-qr-wrap">
                        <QrBlock value={CANDLE_URL} caption="Scan to learn more" />
                    </Element>
                </div>
            </>
        );
    }

    return (
        <>
            <Element dir="top" order={0} count={4} reduce={reduce} className="welcome-logo welcome-logo--sm">
                <img src={STAGE_LOGO} alt="SOLE" />
            </Element>
            <Element dir="left" order={1} count={4} reduce={reduce} className="welcome-headline">
                Join our Discord
            </Element>
            <Element dir="right" order={2} count={4} reduce={reduce} className="welcome-body">
                Stay up to date on the latest announcements and ask questions between meetings.
            </Element>
            <Element dir="bottom" order={3} count={4} reduce={reduce} className="welcome-qr-wrap">
                <QrBlock value={DISCORD_URL} caption="Scan to join" />
            </Element>
        </>
    );
}

const SLIDE_COUNT = 3;

export default function WelcomeScreen() {
    const reduce = useReducedMotion();
    const stageRef = useRef(null);
    const [running, setRunning] = useState(false);
    const [index, setIndex] = useState(0);

    // Preload the heavier images so slides don't pop in blank on the projector.
    useEffect(() => {
        [STAGE_LOGO, CARD_LOGO, CANDLE_IMG].forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    const stop = useCallback(() => {
        setRunning(false);
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => { });
        }
    }, []);

    const start = () => {
        setIndex(0);
        setRunning(true);
        const el = stageRef.current;
        if (el && el.requestFullscreen) {
            el.requestFullscreen().catch(() => { });
        }
    };

    // Advance the loop while running. Pause when the page is hidden so we don't
    // pile up transitions behind a frozen (backgrounded) animation frame loop.
    useEffect(() => {
        if (!running) return undefined;
        const id = setInterval(() => {
            if (document.hidden) return;
            setIndex((i) => (i + 1) % SLIDE_COUNT);
        }, HOLD_MS);
        return () => clearInterval(id);
    }, [running]);

    // Escape / leaving fullscreen stops the loop; lock page scroll while running.
    useEffect(() => {
        if (!running) return undefined;

        const onKey = (e) => {
            if (e.key === "Escape") stop();
        };
        const onFsChange = () => {
            if (!document.fullscreenElement) stop();
        };

        window.addEventListener("keydown", onKey);
        document.addEventListener("fullscreenchange", onFsChange);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", onKey);
            document.removeEventListener("fullscreenchange", onFsChange);
            document.body.style.overflow = prevOverflow;
        };
    }, [running, stop]);

    // Safety net: leave fullscreen if the component unmounts mid-loop.
    useEffect(() => () => {
        if (document.fullscreenElement) document.exitFullscreen().catch(() => { });
    }, []);

    return (
        <div className="welcome-page">
            <Helmet><title>Welcome to SOLE</title></Helmet>

            {!running && (
                <>
                    <Navbar />
                    <div className="welcome-setup">
                        <div className="welcome-setup-card">
                            <img className="welcome-setup-logo" src={CARD_LOGO} alt="SOLE" />
                            <h1>Welcome Screen</h1>
                            <p>
                                A looping welcome for the projector before a talk begins — a greeting,
                                a note about supporting SOLE with an Altar Flame candle, and the Discord invite.
                            </p>
                            <ol className="welcome-setup-list">
                                <li>Welcome &amp; greeting</li>
                                <li>Support us — Altar Flame candle</li>
                                <li>Join our Discord</li>
                            </ol>
                            <button type="button" className="welcome-start-btn" onClick={start}>
                                Start Welcome Screen
                            </button>
                            <p className="welcome-setup-hint">Runs fullscreen and loops. Press <kbd>Esc</kbd> to exit.</p>
                        </div>
                    </div>
                    <Footer />
                </>
            )}

            {/* The looping stage — always mounted so fullscreen can be requested on it,
                hidden and inert until the loop is running. */}
            <div
                ref={stageRef}
                className="welcome-stage"
                data-running={running ? "true" : "false"}
                aria-hidden={running ? "false" : "true"}
            >
                <div className="welcome-sunburst" aria-hidden="true" />

                {running && (
                    <>
                        <AnimatePresence mode="wait" initial>
                            <motion.div
                                key={index}
                                className="welcome-slide"
                                initial={false}
                                exit={{ opacity: 0, transition: { duration: 0.42, ease: EASE_LEAVE } }}
                            >
                                <WelcomeSlide index={index} reduce={reduce} />
                            </motion.div>
                        </AnimatePresence>

                        <div className="welcome-dots" aria-hidden="true">
                            {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
                                <span key={i} className={i === index ? "is-active" : ""} />
                            ))}
                        </div>

                        <button type="button" className="welcome-exit" onClick={stop}>
                            Exit
                        </button>
                    </>
                )}
            </div>

            <style>{`
                .welcome-page { min-height: 100vh; display: flex; flex-direction: column; }

                /* ---------- Setup (normal) view ---------- */
                .welcome-setup {
                    flex: 1 1 auto;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 64px 20px 84px;
                    background: #fbf5ea;
                }
                .welcome-setup-card {
                    width: 100%;
                    max-width: 520px;
                    background: #fff;
                    border: 1px solid #ecdcbf;
                    border-top: 4px solid #c08d3b;
                    border-radius: 18px;
                    box-shadow: 0 18px 46px rgba(15, 54, 80, 0.12);
                    padding: 40px 40px 36px;
                    text-align: center;
                }
                .welcome-setup-logo {
                    width: 92px; height: 92px; object-fit: contain;
                    display: block; margin: 0 auto 16px;
                }
                .welcome-setup-card h1 {
                    font-family: 'EB Garamond', Georgia, serif;
                    font-size: 30px; color: #0f3650; margin: 0 0 12px; font-weight: 600;
                }
                .welcome-setup-card p { color: #4a5763; font-size: 16px; line-height: 1.6; margin: 0 auto 18px; max-width: 400px; }
                .welcome-setup-list {
                    text-align: left; max-width: 320px; margin: 0 auto 22px; padding-left: 20px;
                    color: #4a5763; font-size: 15px; line-height: 1.9;
                }
                .welcome-start-btn {
                    appearance: none; border: 0; cursor: pointer;
                    background: #f1b249; color: #0f3650;
                    font-weight: 800; font-size: 16px;
                    padding: 14px 26px; border-radius: 10px;
                    transition: background 0.15s ease;
                }
                .welcome-start-btn:hover { background: #e7a733; }
                .welcome-setup-hint { font-size: 13px !important; color: #8a94a0 !important; margin-top: 16px !important; }
                .welcome-setup-hint kbd {
                    font-family: inherit; background: #eef1f3; border: 1px solid #d7dde1;
                    border-radius: 5px; padding: 1px 6px; font-size: 12px;
                }

                /* ---------- Fullscreen looping stage ---------- */
                .welcome-stage {
                    position: fixed;
                    inset: 0;
                    z-index: 4000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    color: #ffffff;
                    background:
                        radial-gradient(circle at 50% 0%, #123953, #0f3650 46%, #0d2e4e 100%);
                    font-family: 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif;
                }
                .welcome-stage[data-running="false"] {
                    opacity: 0;
                    visibility: hidden;
                    pointer-events: none;
                }

                /* Radiant sunburst — same fine gold linework as the site hero */
                .welcome-sunburst {
                    position: absolute;
                    left: 50%;
                    top: -46%;
                    width: 150vh;
                    height: 150vh;
                    transform: translateX(-50%);
                    background: repeating-conic-gradient(from 0deg at 50% 50%,
                        rgba(241, 178, 73, 0.13) 0deg 1.5deg, transparent 1.5deg 13deg);
                    -webkit-mask-image: radial-gradient(circle at 50% 50%, #000 0%, transparent 62%);
                    mask-image: radial-gradient(circle at 50% 50%, #000 0%, transparent 62%);
                    pointer-events: none;
                    animation: welcomeSunburstBreath 22s ease-in-out infinite alternate;
                }
                @keyframes welcomeSunburstBreath {
                    from { transform: translateX(-50%) scale(1); opacity: 0.82; }
                    to   { transform: translateX(-50%) scale(1.045); opacity: 1; }
                }

                .welcome-slide {
                    position: relative;
                    z-index: 1;
                    width: min(1180px, 90vw);
                    padding: 3vh 2vw clamp(56px, 9vh, 96px);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: clamp(12px, 2.6vh, 30px);
                }

                .welcome-logo img {
                    width: clamp(112px, 15vh, 190px);
                    height: auto;
                    display: block;
                }
                .welcome-logo--sm img { width: clamp(84px, 10vh, 130px); }

                .welcome-headline {
                    font-weight: 800;
                    font-size: clamp(2.4rem, 7vw, 6.2rem);
                    line-height: 1.05;
                    letter-spacing: -0.01em;
                    color: #ffffff;
                    max-width: 18ch;
                }
                .welcome-body {
                    font-weight: 500;
                    font-size: clamp(1.15rem, 2.5vw, 2.15rem);
                    line-height: 1.42;
                    color: #f7d09a;
                    max-width: 30ch;
                }
                .welcome-note {
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.22em;
                    font-size: clamp(0.85rem, 1.7vw, 1.35rem);
                    color: #f1b249;
                }

                .welcome-media-row {
                    /* candle photo and QR share one size so their frames line up */
                    --media-size: clamp(150px, 24vh, 260px);
                    --media-pad: clamp(12px, 2vh, 22px);
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                    gap: clamp(20px, 3.5vw, 52px);
                    flex-wrap: wrap;
                    margin-top: clamp(6px, 1.4vh, 18px);
                }
                .welcome-candle {
                    background: #0d2e4e;
                    border: 3px solid #c08d3b;
                    border-radius: 10px;
                    padding: var(--media-pad);
                    box-shadow: 0 16px 46px rgba(0, 0, 0, 0.42);
                    line-height: 0;
                }
                .welcome-candle img {
                    width: var(--media-size);
                    height: var(--media-size);
                    object-fit: cover;
                    border-radius: 4px;
                    display: block;
                }

                .welcome-qr { display: flex; flex-direction: column; align-items: center; gap: clamp(8px, 1.5vh, 16px); }
                .welcome-qr-frame {
                    background: #fbf5ea;
                    border: 3px solid #c08d3b;
                    border-radius: 10px;
                    padding: var(--media-pad);
                    box-shadow: 0 16px 46px rgba(0, 0, 0, 0.42);
                    line-height: 0;
                }
                .welcome-qr-frame svg {
                    width: var(--media-size);
                    height: var(--media-size);
                    display: block;
                }
                .welcome-qr-caption {
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.18em;
                    font-size: clamp(0.9rem, 1.7vw, 1.4rem);
                    color: #f1b249;
                }

                .welcome-dots {
                    position: absolute;
                    left: 50%;
                    bottom: clamp(12px, 1.8vh, 20px);
                    transform: translateX(-50%);
                    display: flex;
                    gap: 12px;
                    z-index: 2;
                }
                .welcome-dots span {
                    width: 9px; height: 9px; border-radius: 999px;
                    background: rgba(241, 178, 73, 0.28);
                    transition: background 0.4s ease, transform 0.4s ease;
                }
                .welcome-dots span.is-active {
                    background: #f1b249;
                    transform: scale(1.25);
                }

                .welcome-exit {
                    position: absolute;
                    top: clamp(16px, 3vh, 30px);
                    right: clamp(16px, 3vw, 34px);
                    z-index: 2;
                    appearance: none;
                    cursor: pointer;
                    background: rgba(13, 46, 78, 0.55);
                    color: rgba(247, 208, 154, 0.72);
                    border: 1px solid rgba(241, 178, 73, 0.32);
                    border-radius: 999px;
                    padding: 8px 18px;
                    font: inherit;
                    font-size: 0.9rem;
                    font-weight: 700;
                    letter-spacing: 0.14em;
                    text-transform: uppercase;
                    transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
                }
                .welcome-exit:hover {
                    color: #0f3650;
                    background: #f1b249;
                    border-color: #f1b249;
                }

                @media (prefers-reduced-motion: reduce) {
                    .welcome-sunburst { animation: none; }
                }

                @media (max-width: 520px) {
                    .welcome-setup-card { padding: 34px 22px 30px; }
                    .welcome-setup-card h1 { font-size: 25px; }
                    .welcome-start-btn { width: 100%; }
                }
            `}</style>
        </div>
    );
}
